import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  MapPin,
  Layers,
  Database,
  Cog,
  TrendingUp,
  Users,
  Briefcase,
  Heart,
  GraduationCap,
  Landmark,
  Droplets,
  Globe2,
  Flag,
  Globe,
  Wallet,
  Scale,
  UserCheck,
  HeartPulse,
  FileText,
  Bot,
  HandshakeIcon,
  Workflow,
  ShieldCheck,
  ScrollText,
  LineChart,
  ClipboardCheck,
  IdCard,
  Fingerprint,
  Newspaper,
  Check,
  CheckCircle2,
  Loader2,
  Cpu,
  Database as DbIcon,
  Gavel,
  AlertTriangle,
  CalendarClock,
  History as HistoryIcon,
} from "lucide-react";
import CircleGlow from "../components/CircleGlow";
import {
  saveAssessment,
  loadSystemForEdit,
  applySystemUpdate,
} from "../lib/assessmentStore";
import { createAssessment } from "../lib/api";

/**
 * /wizard — full 6-step intake.
 * Step state, form state, validation, transitions, and the "Berekenen..."
 * loading state on the final step all live here.
 */

const STEPS = [
  { num: "01", id: "organisatie", label: "Organisatie", icon: Building2 },
  { num: "02", id: "geografie", label: "Geografie", icon: MapPin },
  { num: "03", id: "toepassingen", label: "Toepassingen", icon: Layers },
  { num: "04", id: "data", label: "Data", icon: Database },
  { num: "05", id: "inzet", label: "Inzet", icon: Cog },
  { num: "06", id: "volwassenheid", label: "Volwassenheid", icon: TrendingUp },
];

const SIZES = [
  { id: "1-10", label: "1 — 10" },
  { id: "11-50", label: "11 — 50" },
  { id: "51-250", label: "51 — 250" },
  { id: "251-1000", label: "251 — 1.000" },
  { id: "1001-10000", label: "1.001 — 10.000" },
  { id: "10000+", label: "10.000+" },
];

const SECTORS = [
  { id: "gemeente", label: "Gemeente", icon: Landmark },
  { id: "provincie", label: "Provincie", icon: Briefcase },
  { id: "rijk", label: "Rijksoverheid", icon: Briefcase },
  { id: "waterschap", label: "Waterschap", icon: Droplets },
  { id: "zorg", label: "Zorg", icon: Heart },
  { id: "onderwijs", label: "Onderwijs", icon: GraduationCap },
];

const REGIONS = [
  {
    id: "nl",
    label: "Nederland",
    subtitle: "Binnenlandse inzet · AVG, AWB, sectorale wetten",
    icon: Flag,
  },
  {
    id: "eu",
    label: "Europese Unie",
    subtitle: "AI-Verordening · GDPR · grensoverschrijdend",
    icon: Globe2,
  },
  {
    id: "world",
    label: "Wereldwijd",
    subtitle: "Datadoorgifte buiten EU · transfer impact assessment",
    icon: Globe,
  },
];

const TOEPASSINGEN = [
  {
    id: "sociaal",
    label: "Sociaal Domein",
    subtitle: "Uitkeringen · Schuldhulp",
    icon: Wallet,
  },
  {
    id: "handhaving",
    label: "Handhaving & Openbare Ruimte",
    subtitle: "APV · Omgevingswet",
    icon: Scale,
  },
  {
    id: "hr",
    label: "HR & Werving",
    subtitle: "Selectie · matching",
    icon: UserCheck,
  },
  {
    id: "zorg",
    label: "Gezondheidszorg",
    subtitle: "Triage · indicatiestelling",
    icon: HeartPulse,
  },
  {
    id: "informatie",
    label: "Informatiebeheer",
    subtitle: "Woo · archief · classificatie",
    icon: FileText,
  },
];

const DATA_TYPES = [
  {
    id: "personal",
    label: "Persoonsgegevens",
    subtitle: "NAW, BSN, contactgegevens",
    icon: IdCard,
  },
  {
    id: "special",
    label: "Bijzondere persoonsgegevens",
    subtitle: "bijv. gezondheid, etniciteit, religie",
    icon: HeartPulse,
  },
  {
    id: "biometric",
    label: "Biometrische data",
    subtitle: "Gezicht, vingerafdruk, stem",
    icon: Fingerprint,
  },
  {
    id: "public",
    label: "Publieke data",
    subtitle: "Niet-gevoelig · open data",
    icon: Newspaper,
  },
];

const INZET_MODES = [
  {
    id: "auto",
    label: "Volledig geautomatiseerde besluitvorming",
    subtitle: "Geen menselijke tussenkomst · AVG art. 22",
    icon: Bot,
  },
  {
    id: "support",
    label: "Besluitondersteuning voor ambtenaren",
    subtitle: "Human-in-the-loop · advies, geen besluit",
    icon: HandshakeIcon,
  },
  {
    id: "process",
    label: "Interne procesoptimalisatie",
    subtitle: "Routering, prioritering, planning",
    icon: Workflow,
  },
];

const SAFEGUARDS = [
  {
    id: "fg",
    label: "FG / DPO aangesteld",
    subtitle: "Functionaris gegevensbescherming actief",
    icon: ShieldCheck,
  },
  {
    id: "policy",
    label: "AI-beleid vastgesteld",
    subtitle: "Bestuurlijk kader · besluit college / raad",
    icon: ScrollText,
  },
  {
    id: "monitoring",
    label: "Continue monitoring op bias",
    subtitle: "Drift- en fairness-dashboard live",
    icon: LineChart,
  },
  {
    id: "dpia",
    label: "DPIA uitgevoerd",
    subtitle: "Gegevensbeschermingseffectbeoordeling getekend",
    icon: ClipboardCheck,
  },
];

const VERSION_REASONS = [
  {
    id: "model-update",
    label: "Model-update",
    subtitle: "Nieuwe modelversie, retraining of architectuurwijziging",
    icon: Cpu,
  },
  {
    id: "nieuwe-databron",
    label: "Nieuwe databron",
    subtitle: "Toegevoegde of gewijzigde dataset / feature",
    icon: DbIcon,
  },
  {
    id: "wettelijke-wijziging",
    label: "Wettelijke wijziging",
    subtitle: "Nieuwe regelgeving · jurisprudentie · sectorbesluit",
    icon: Gavel,
  },
  {
    id: "geconstateerd-incident",
    label: "Geconstateerd incident",
    subtitle: "Bias-signaal, klacht, datalek of operationele storing",
    icon: AlertTriangle,
  },
  {
    id: "periodieke-herbeoordeling",
    label: "Periodieke herbeoordeling",
    subtitle: "Reguliere review per kwartaal / jaar",
    icon: CalendarClock,
  },
];

const Wizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(null); // { isSeed, displayName }
  const [versionReason, setVersionReason] = useState("");

  const [form, setForm] = useState({
    orgName: "Gemeente Leeuwarden",
    systemName: "",
    size: "",
    sector: "gemeente",
    regions: [],
    toepassing: "",
    dataTypes: [],
    inzet: "",
    safeguards: [],
  });

  // Pre-fill the form when in edit mode
  useEffect(() => {
    if (!editId) return;
    const snap = loadSystemForEdit(editId);
    if (!snap) {
      navigate("/assessment-choice", { replace: true });
      return;
    }
    setForm(snap.form);
    setEditMode({ isSeed: snap.isSeed, displayName: snap.displayName });
  }, [editId, navigate]);

  const setField = useCallback(
    (k, v) => setForm((f) => ({ ...f, [k]: v })),
    []
  );

  const toggleListItem = useCallback(
    (k, id) =>
      setForm((f) => ({
        ...f,
        [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id],
      })),
    []
  );

  const isStepValid = useMemo(
    () => (step) => {
      if (step === 0)
        return (
          form.orgName.trim().length > 1 &&
          form.systemName.trim().length > 1 &&
          !!form.size &&
          !!form.sector
        );
      if (step === 1) return form.regions.length > 0;
      if (step === 2) return !!form.toepassing;
      if (step === 3) return form.dataTypes.length > 0;
      if (step === 4) return !!form.inzet;
      if (step === 5) {
        const safeguardsOk = form.safeguards.length > 0;
        // In edit mode, also require a version reason
        return editMode ? safeguardsOk && !!versionReason : safeguardsOk;
      }
      return true;
    },
    [form, editMode, versionReason]
  );

  const progressPct = Math.round(((currentStep + 1) / STEPS.length) * 100);

  const goNext = () => {
    if (!isStepValid(currentStep) || submitting) return;
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Final step → "Berekenen..." for 2s, then route to the System Result page
    setSubmitting(true);
    if (editMode && editId) {
      const reasonLabel =
        VERSION_REASONS.find((v) => v.id === versionReason)?.label ||
        versionReason;
      applySystemUpdate(editId, form, reasonLabel, editMode.isSeed);
      setTimeout(() => {
        navigate(`/platform/${editId}`);
      }, 2000);
      return;
    }
    // Persist to the backend (canonical assessment + LaTeX report) and
    // keep the client-side dossier for the VNG lifecycle / evidence /
    // roles / audit UX the backend does not model yet. The backend id is
    // stored on the local record so the report PDF can be fetched from
    // GET /api/reports/{backendId}.pdf. Backend failures degrade
    // gracefully — the dossier still opens, only the server report is
    // unavailable.
    createAssessment(form)
      .then((assessment) => {
        const record = saveAssessment(form, {
          backendId: assessment?.id || "",
        });
        setTimeout(() => {
          navigate(`/platform/${record.id}`);
        }, 2000);
      });
  };

  const goBack = () => {
    if (submitting) return;
    if (currentStep === 0) {
      navigate("/assessment-choice");
    } else {
      setCurrentStep((s) => s - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const step = STEPS[currentStep];
  const isLast = currentStep === STEPS.length - 1;

  return (
    <div data-testid="page-wizard">
      <section className="relative overflow-hidden">
        <CircleGlow intensity={0.7} />

        <div className="rk-container relative pt-10 md:pt-14 pb-24">
          {/* Top meta */}
          <div className="flex items-center justify-between mb-8">
            <button
              type="button"
              onClick={goBack}
              data-testid="wizard-back"
              disabled={submitting}
              className="flex items-center gap-2 text-[#D4D8DF] hover:text-white text-[12px] tracking-[0.2em] uppercase disabled:opacity-40"
            >
              <ArrowLeft strokeWidth={1.25} className="w-4 h-4" />
              {currentStep === 0 ? "Terug naar keuze" : "Vorige stap"}
            </button>
            <div className="overline" data-testid="wizard-step-counter">
              Stap {currentStep + 1} / {STEPS.length} · {step.label} ·{" "}
              {progressPct}%
            </div>
          </div>

          {/* Edit-mode banner */}
          {editMode && (
            <div
              className="mb-6 px-5 py-4 flex items-center gap-3 border border-[#5BC0EB]/35"
              data-testid="wizard-edit-banner"
              style={{
                background:
                  "linear-gradient(120deg, rgba(91,192,235,0.10) 0%, rgba(91,192,235,0) 60%)",
              }}
            >
              <HistoryIcon
                strokeWidth={1.25}
                className="w-4 h-4 text-[#7FD7E4] shrink-0"
              />
              <div className="text-sm text-[#D4D8DF]">
                <span className="text-[#7FD7E4]">Update-modus</span> · u
                herziet{" "}
                <span className="text-white">{editMode.displayName}</span>. De
                antwoorden zijn pre-ingevuld — pas alleen aan wat veranderd
                is.
              </div>
            </div>
          )}

          {/* Progress bar */}
          <div
            className="h-px bg-[#C0C5CE]/20 relative mb-10"
            data-testid="wizard-progress"
          >
            <div
              data-testid="wizard-progress-fill"
              className="absolute left-0 top-0 h-px bg-[#5BC0EB]"
              style={{
                width: `${progressPct}%`,
                boxShadow: "0 0 6px rgba(91,192,235,0.6)",
                transition: "width 600ms ease",
              }}
            />
          </div>

          {/* Step indicators */}
          <ol
            className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-14"
            data-testid="wizard-steps"
          >
            {STEPS.map((s, i) => {
              const isActive = i === currentStep;
              const isDone = i < currentStep;
              return (
                <li
                  key={s.id}
                  data-testid={`wizard-step-indicator-${s.id}`}
                  data-active={isActive}
                  data-done={isDone}
                  className="flex items-center gap-3"
                >
                  <span
                    className="w-9 h-9 flex items-center justify-center shrink-0 border transition-all duration-500"
                    style={{
                      borderColor: isActive
                        ? "#5BC0EB"
                        : isDone
                        ? "#C0C5CE"
                        : "rgba(192,197,206,0.3)",
                      color: isActive
                        ? "#5BC0EB"
                        : isDone
                        ? "#1C3A5A"
                        : "#C0C5CE",
                      fontFamily: "Fraunces, serif",
                      fontWeight: 400,
                      background: isActive
                        ? "rgba(91,192,235,0.08)"
                        : isDone
                        ? "#C0C5CE"
                        : "transparent",
                    }}
                  >
                    {isDone ? (
                      <Check strokeWidth={1.5} className="w-4 h-4" />
                    ) : (
                      s.num
                    )}
                  </span>
                  <span
                    className="text-[11px] tracking-[0.18em] uppercase transition-colors duration-500"
                    style={{
                      color: isActive
                        ? "#FFFFFF"
                        : isDone
                        ? "#D4D8DF"
                        : "#9AA4B2",
                    }}
                  >
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ol>

          {/* Cockpit form panel — re-keyed per step for fade-in */}
          <div
            data-testid="wizard-panel"
            key={`panel-${currentStep}`}
            className="relative rk-fade-up"
            style={{
              background: "#EEF1F5",
              color: "#0F1E33",
              padding: "3rem 2.25rem",
              border: "1px solid rgba(15, 30, 51, 0.08)",
              boxShadow: "0 30px 60px -30px rgba(0,0,0,0.4)",
            }}
          >
            {currentStep === 0 && (
              <StepOrganisatie form={form} setField={setField} />
            )}
            {currentStep === 1 && (
              <StepGeografie
                form={form}
                onToggle={(id) => toggleListItem("regions", id)}
              />
            )}
            {currentStep === 2 && (
              <StepToepassingen form={form} setField={setField} />
            )}
            {currentStep === 3 && (
              <StepData
                form={form}
                onToggle={(id) => toggleListItem("dataTypes", id)}
              />
            )}
            {currentStep === 4 && (
              <StepInzet form={form} setField={setField} />
            )}
            {currentStep === 5 && (
              <StepVolwassenheid
                form={form}
                onToggle={(id) => toggleListItem("safeguards", id)}
                editMode={editMode}
                versionReason={versionReason}
                setVersionReason={setVersionReason}
              />
            )}

            {/* Footer */}
            <div
              className="mt-14 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
              style={{ borderTop: "1px solid rgba(15,30,51,0.1)" }}
            >
              <button
                type="button"
                onClick={() => navigate("/assessment-choice")}
                data-testid="wizard-cancel"
                disabled={submitting}
                className="text-[12px] tracking-[0.2em] uppercase disabled:opacity-40"
                style={{ color: "#5470A3" }}
              >
                Annuleren
              </button>
              <div className="flex items-center gap-4">
                <span
                  className="text-[11px] tracking-[0.18em] uppercase"
                  style={{ color: "#5470A3" }}
                >
                  {submitting
                    ? "Een moment geduld"
                    : "Voortgang automatisch opgeslagen"}
                </span>
                <button
                  type="button"
                  disabled={!isStepValid(currentStep) || submitting}
                  data-testid="wizard-next"
                  className="rk-btn-cyan"
                  onClick={goNext}
                  style={{
                    opacity:
                      !isStepValid(currentStep) || submitting ? 0.7 : 1,
                    cursor:
                      !isStepValid(currentStep) || submitting
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {submitting ? (
                    <>
                      <Loader2
                        strokeWidth={1.5}
                        className="w-4 h-4 animate-spin"
                      />
                      Berekenen...
                    </>
                  ) : (
                    <>
                      {isLast
                        ? editMode
                          ? "Update afronden"
                          : "Assessment afronden"
                        : "Volgende stap"}
                      <ArrowRight strokeWidth={1.25} className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <p
            className="mt-8 text-sm text-[#9AA4B2] max-w-3xl flex items-center gap-2"
            data-testid="wizard-reassurance"
          >
            <Users
              strokeWidth={1}
              className="w-4 h-4 text-[#C0C5CE] shrink-0"
            />
            Uw antwoorden worden conform AVG verwerkt en delen we niet zonder
            uw expliciete toestemming.
          </p>
        </div>
      </section>
    </div>
  );
};

// =================== Step bodies ===================

const StepOrganisatie = ({ form, setField }) => (
  <>
    <StepHeader
      eyebrow="Stap 1 / 6 · Organisatie"
      title="Vertel ons over uw"
      titleEm="organisatie."
      subtitle="We gebruiken dit om te bepalen welke verplichtingen écht op u van toepassing zijn."
    />
    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 max-w-5xl">
      <Field label="Naam van de organisatie" testid="field-orgname">
        <input
          type="text"
          value={form.orgName}
          onChange={(e) => setField("orgName", e.target.value)}
          data-testid="input-orgname"
          className="rk-light-input"
        />
      </Field>
      <Field label="Naam van het AI-systeem" testid="field-systemname">
        <input
          type="text"
          value={form.systemName}
          onChange={(e) => setField("systemName", e.target.value)}
          placeholder="bijv. Fraude-detectie v1"
          data-testid="input-systemname"
          className="rk-light-input"
        />
      </Field>
      <Field label="Omvang (medewerkers)" testid="field-size" fullWidth>
        <SelectGrid
          items={SIZES}
          value={form.size}
          onChange={(id) => setField("size", id)}
          renderItem={(s, active) => (
            <>
              <span style={{ fontFamily: "Fraunces, serif" }}>{s.label}</span>
              <span className="rk-choice__sub">medewerkers</span>
            </>
          )}
          testidPrefix="size"
        />
      </Field>
      <Field label="Sector" testid="field-sector" fullWidth>
        <SelectGrid
          items={SECTORS}
          value={form.sector}
          onChange={(id) => setField("sector", id)}
          renderItem={(s, active) => {
            const Icon = s.icon;
            return (
              <>
                <Icon
                  strokeWidth={1}
                  className="w-5 h-5 mb-2"
                  style={{ color: active ? "#1C3A5A" : "#5470A3" }}
                />
                <span style={{ fontFamily: "Fraunces, serif" }}>{s.label}</span>
              </>
            );
          }}
          testidPrefix="sector"
        />
      </Field>
    </div>
  </>
);

const StepGeografie = ({ form, onToggle }) => (
  <>
    <StepHeader
      eyebrow="Stap 2 / 6 · Geografie"
      title="In welke regio's wordt het systeem"
      titleEm="ingezet?"
      subtitle="Meerdere antwoorden mogelijk. Dit bepaalt of de AI-Verordening en eventuele datadoorgifteregels van toepassing zijn."
    />
    <CheckGrid
      items={REGIONS}
      values={form.regions}
      onToggle={onToggle}
      testidPrefix="region"
      cols={3}
    />
    <HelpLine>Tip: kies minstens één regio om verder te gaan.</HelpLine>
  </>
);

const StepToepassingen = ({ form, setField }) => (
  <>
    <StepHeader
      eyebrow="Stap 3 / 6 · Toepassingen"
      title="Wat is het primaire doel van het"
      titleEm="AI-systeem?"
      subtitle="Kies het hoofddomein. We bepalen op basis hiervan welke sectorwetten meewegen in uw assessment."
    />
    <RadioGrid
      items={TOEPASSINGEN}
      value={form.toepassing}
      onChange={(id) => setField("toepassing", id)}
      testidPrefix="toepassing"
      cols={3}
    />
    <HelpLine>Eén keuze. U kunt later subdoelen toevoegen.</HelpLine>
  </>
);

const StepData = ({ form, onToggle }) => (
  <>
    <StepHeader
      eyebrow="Stap 4 / 6 · Data"
      title="Welke soorten data verwerkt het"
      titleEm="systeem?"
      subtitle="Meerdere antwoorden mogelijk. Bijzondere of biometrische gegevens vragen om extra waarborgen."
    />
    <CheckGrid
      items={DATA_TYPES}
      values={form.dataTypes}
      onToggle={onToggle}
      testidPrefix="datatype"
      cols={2}
    />
    <HelpLine>Selecteer alle datacategorieën die het systeem raakt.</HelpLine>
  </>
);

const StepInzet = ({ form, setField }) => (
  <>
    <StepHeader
      eyebrow="Stap 5 / 6 · Inzet"
      title="Hoe wordt de AI ingezet in het"
      titleEm="beslisproces?"
      subtitle="De inzet bepaalt het risiconiveau en of er een menselijke tussenkomst verplicht is."
    />
    <RadioGrid
      items={INZET_MODES}
      value={form.inzet}
      onChange={(id) => setField("inzet", id)}
      testidPrefix="inzet"
      cols={1}
      tall
    />
    <HelpLine>Eén keuze die de hoofdinzet het beste beschrijft.</HelpLine>
  </>
);

const StepVolwassenheid = ({
  form,
  onToggle,
  editMode,
  versionReason,
  setVersionReason,
}) => (
  <>
    <StepHeader
      eyebrow="Stap 6 / 6 · Volwassenheid"
      title="Welke waarborgen zijn momenteel"
      titleEm="aanwezig?"
      subtitle="Vink aan wat al staat. We bepalen op basis hiervan uw startpositie in de readiness-score."
    />
    <CheckGrid
      items={SAFEGUARDS}
      values={form.safeguards}
      onToggle={onToggle}
      testidPrefix="safeguard"
      cols={2}
    />
    <HelpLine>Hoe vollediger uw antwoord, hoe nauwkeuriger de uitkomst.</HelpLine>

    {/* Versiebeheer section — only in update mode */}
    {editMode && (
      <div
        className="mt-14 pt-10"
        style={{ borderTop: "1px solid rgba(15,30,51,0.1)" }}
        data-testid="wizard-versiebeheer"
      >
        <div
          className="text-[11px] tracking-[0.22em] uppercase mb-3"
          style={{ color: "#5470A3" }}
        >
          Versiebeheer · verplicht bij update
        </div>
        <h2
          className="text-3xl md:text-4xl tracking-tight leading-tight"
          style={{
            fontFamily: "Fraunces, serif",
            fontWeight: 300,
            color: "#0F1E33",
          }}
        >
          Waarom voert u deze{" "}
          <em
            className="italic"
            style={{
              fontFamily: "Fraunces, serif",
              fontWeight: 300,
              color: "#1C3A5A",
            }}
          >
            update uit?
          </em>
        </h2>
        <p
          className="mt-4 max-w-3xl"
          style={{ color: "#3A4D6B" }}
        >
          Dit wordt vastgelegd in de versie-historie van het systeem en is
          zichtbaar in het publieke transparantie-dossier.
        </p>
        <RadioGrid
          items={VERSION_REASONS}
          value={versionReason}
          onChange={setVersionReason}
          testidPrefix="versiebeheer"
          cols={2}
        />
        <HelpLine>
          Eén reden volstaat — combineer eventueel met een toelichting in het
          dossier.
        </HelpLine>
      </div>
    )}
  </>
);

// =================== Reusable building blocks ===================

const StepHeader = ({ eyebrow, title, titleEm, subtitle }) => (
  <div className="max-w-3xl">
    <div
      className="text-[11px] tracking-[0.22em] uppercase mb-3"
      style={{ color: "#5470A3" }}
    >
      {eyebrow}
    </div>
    <h1
      className="text-4xl md:text-5xl tracking-tight leading-tight"
      style={{
        fontFamily: "Fraunces, serif",
        fontWeight: 300,
        color: "#0F1E33",
      }}
      data-testid="wizard-title"
    >
      {title}{" "}
      <em
        className="italic"
        style={{
          fontFamily: "Fraunces, serif",
          fontWeight: 300,
          color: "#1C3A5A",
        }}
      >
        {titleEm}
      </em>
    </h1>
    <p
      className="mt-5 text-base md:text-lg leading-relaxed"
      style={{ color: "#3A4D6B" }}
      data-testid="wizard-subtitle"
    >
      {subtitle}
    </p>
  </div>
);

const Field = ({ label, testid, fullWidth, children }) => (
  <label
    data-testid={testid}
    className={`flex flex-col gap-3 ${fullWidth ? "md:col-span-2" : ""}`}
  >
    <span
      className="text-[10px] tracking-[0.28em] uppercase"
      style={{ color: "#5470A3", fontWeight: 500 }}
    >
      {label}
    </span>
    {children}
  </label>
);

const HelpLine = ({ children }) => (
  <p
    className="mt-6 text-sm"
    style={{ color: "#5470A3" }}
    data-testid="step-help"
  >
    {children}
  </p>
);

// Single-select grid (used for size/sector)
const SelectGrid = ({ items, value, onChange, renderItem, testidPrefix }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
    {items.map((it) => {
      const active = value === it.id;
      return (
        <button
          key={it.id}
          type="button"
          onClick={() => onChange(it.id)}
          data-testid={`${testidPrefix}-${it.id}`}
          className="rk-choice"
          data-active={active}
        >
          {renderItem(it, active)}
        </button>
      );
    })}
  </div>
);

// Multi-select grid with a top-right checkbox
const CheckGrid = ({ items, values, onToggle, testidPrefix, cols = 3 }) => {
  const colClass =
    cols === 1
      ? "grid-cols-1"
      : cols === 2
      ? "grid-cols-1 md:grid-cols-2"
      : "grid-cols-1 md:grid-cols-3";
  return (
    <div className={`mt-12 max-w-5xl grid ${colClass} gap-3`}>
      {items.map((it) => {
        const Icon = it.icon;
        const active = values.includes(it.id);
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onToggle(it.id)}
            data-testid={`${testidPrefix}-${it.id}`}
            className="rk-choice"
            data-active={active}
            style={{ minHeight: 124, padding: "1.2rem 1.2rem" }}
            aria-pressed={active}
          >
            <div className="w-full flex items-start justify-between mb-2">
              <Icon
                strokeWidth={1}
                className="w-6 h-6"
                style={{ color: active ? "#1C3A5A" : "#5470A3" }}
              />
              <span
                data-testid={`${testidPrefix}-check-${it.id}`}
                className="w-5 h-5 flex items-center justify-center"
                style={{
                  border: `1px solid ${
                    active ? "#5BC0EB" : "rgba(15,30,51,0.25)"
                  }`,
                  background: active ? "#5BC0EB" : "#FFFFFF",
                  color: active ? "#FFFFFF" : "transparent",
                  transition:
                    "background-color 200ms ease, border-color 200ms ease",
                }}
              >
                <CheckCircle2 strokeWidth={2} className="w-4 h-4" />
              </span>
            </div>
            <span
              className="text-lg"
              style={{ fontFamily: "Fraunces, serif", fontWeight: 400 }}
            >
              {it.label}
            </span>
            <span className="rk-choice__sub mt-1">{it.subtitle}</span>
          </button>
        );
      })}
    </div>
  );
};

// Single-select large radio-style grid
const RadioGrid = ({ items, value, onChange, testidPrefix, cols = 2, tall }) => {
  const colClass =
    cols === 1
      ? "grid-cols-1"
      : cols === 2
      ? "grid-cols-1 md:grid-cols-2"
      : "grid-cols-1 md:grid-cols-3";
  return (
    <div className={`mt-12 max-w-5xl grid ${colClass} gap-3`}>
      {items.map((it) => {
        const Icon = it.icon;
        const active = value === it.id;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onChange(it.id)}
            data-testid={`${testidPrefix}-${it.id}`}
            className="rk-choice"
            data-active={active}
            style={{
              minHeight: tall ? 120 : 130,
              padding: "1.2rem 1.2rem",
            }}
            aria-pressed={active}
          >
            <div className="w-full flex items-start justify-between mb-2">
              <Icon
                strokeWidth={1}
                className="w-6 h-6"
                style={{ color: active ? "#1C3A5A" : "#5470A3" }}
              />
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center"
                style={{
                  border: `1px solid ${
                    active ? "#5BC0EB" : "rgba(15,30,51,0.25)"
                  }`,
                  background: active ? "#5BC0EB" : "#FFFFFF",
                  transition:
                    "background-color 200ms ease, border-color 200ms ease",
                }}
              >
                {active && (
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: "#FFFFFF" }}
                  />
                )}
              </span>
            </div>
            <span
              className="text-lg"
              style={{ fontFamily: "Fraunces, serif", fontWeight: 400 }}
            >
              {it.label}
            </span>
            <span className="rk-choice__sub mt-1">{it.subtitle}</span>
          </button>
        );
      })}
    </div>
  );
};

export default Wizard;
